/**
 * useAppMode Hook
 *
 * React hook for accessing and managing the app mode state.
 * Provides the current mode, feature access checks, and pregnancy data.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePregnancyStore, AppMode, Pregnancy } from '@/store/pregnancyStore';
import {
  fetchAppMode,
  fetchLastCompletedPregnancy,
  canAccessFeature as checkFeatureAccess,
  completePregnancyBirth,
  completePregnancyEnded,
} from '@/services/appModeService';
import { generateBabyAppointmentSuggestions } from '@/services/suggestedAppointmentsService';
import { supabase } from '@/lib/supabase';

interface UseAppModeReturn {
  // Current state
  appMode: AppMode;
  isLoading: boolean;
  currentPregnancy: Pregnancy | null;
  lastCompletedPregnancy: Pregnancy | null;

  // Feature access
  canAccessFeature: (feature: string) => boolean;
  isPregnancyMode: boolean;
  isIdleMode: boolean;
  isIdle: boolean;
  isBabyMode: boolean;

  // Time since birth (for birth completion mode)
  daysSinceBirth: { days: number; weeks: number; years: number } | null;

  // Actions
  refreshAppMode: () => Promise<void>;
  completeBirth: (
    birthDate: string,
    birthTime?: string,
    babyData?: Record<string, unknown>
  ) => Promise<boolean>;
  endPregnancy: () => Promise<boolean>;
}

export function useAppMode(): UseAppModeReturn {
  const {
    currentPregnancy,
    lastCompletedPregnancy,
    appMode,
    setAppMode,
    setLastCompletedPregnancy,
    getDaysSinceBirth,
  } = usePregnancyStore();

  const [isLoading, setIsLoading] = useState(true);

  // Auf true gesetzt im Cleanup des Mount-Effekts weiter unten. Verhindert,
  // dass ein Fetch, der erst nach dem Unmount der aufrufenden Komponente
  // aufloest (z.B. schneller Seitenwechsel Home -> Tools), noch nachtraeglich
  // appMode/lastCompletedPregnancy im globalen Store umschreibt - genau das
  // war eine der Ursachen fuer das Flackern der Tool-Liste unter /tools: eine
  // laengst unmountete HomePage schrieb appMode um, waehrend ToolsPage schon
  // rendert.
  const cancelledRef = useRef(false);

  // Load app mode on mount and when user changes
  const refreshAppMode = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelledRef.current) return;
      if (!user) {
        setAppMode('idle');
        setIsLoading(false);
        return;
      }

      // Fetch app mode from database
      const mode = await fetchAppMode(user.id);
      if (cancelledRef.current) return;
      setAppMode(mode);

      // If idle mode, fetch last completed pregnancy
      if (mode === 'idle' || mode === 'baby') {
        const lastPregnancy = await fetchLastCompletedPregnancy(user.id);
        if (cancelledRef.current) return;
        setLastCompletedPregnancy(lastPregnancy);
      }
    } catch (error) {
      if (cancelledRef.current) return;
      console.error('Error refreshing app mode:', error);
      setAppMode('idle');
    } finally {
      setIsLoading(false);
    }
  }, [setAppMode, setLastCompletedPregnancy]);

  // Initial load
  useEffect(() => {
    cancelledRef.current = false;
    refreshAppMode();
    return () => {
      cancelledRef.current = true;
    };
  }, [refreshAppMode]);

  // Feature access check
  const canAccessFeature = useCallback(
    (feature: string): boolean => {
      return checkFeatureAccess(feature, appMode);
    },
    [appMode]
  );

  // Complete pregnancy with birth
  const completeBirth = useCallback(
    async (
      birthDate: string,
      birthTime?: string,
      babyData?: Record<string, unknown>
    ): Promise<boolean> => {
      if (!currentPregnancy) return false;

      const success = await completePregnancyBirth(
        currentPregnancy.id,
        birthDate,
        birthTime,
        babyData
      );

      if (success) {
        // Generate suggested appointments (U-Untersuchungen, vaccinations, etc.)
        try {
          await generateBabyAppointmentSuggestions(
            currentPregnancy.id,
            new Date(birthDate)
          );
        } catch (error) {
          // Log but don't fail the birth completion
          console.error('Error generating baby appointment suggestions:', error);
        }

        await refreshAppMode();
      }

      return success;
    },
    [currentPregnancy, refreshAppMode]
  );

  // End pregnancy (neutral)
  const endPregnancy = useCallback(async (): Promise<boolean> => {
    if (!currentPregnancy) return false;

    const success = await completePregnancyEnded(currentPregnancy.id);

    if (success) {
      await refreshAppMode();
    }

    return success;
  }, [currentPregnancy, refreshAppMode]);

  return {
    // Current state
    appMode,
    isLoading,
    currentPregnancy,
    lastCompletedPregnancy,

    // Feature access
    canAccessFeature,
    isPregnancyMode: appMode === 'pregnancy',
    isIdleMode: appMode === 'idle' || appMode === 'baby',
    isIdle: appMode === 'idle',
    isBabyMode: appMode === 'baby',

    // Time since birth
    daysSinceBirth: getDaysSinceBirth(),

    // Actions
    refreshAppMode,
    completeBirth,
    endPregnancy,
  };
}

export default useAppMode;
